import { useSortable } from '@dnd-kit/sortable';
import {
  ChatCircleIcon,
  DotsSixVerticalIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';

import {
  Avatar,
  DropdownMenu,
  DropdownMenuItem,
  Text,
} from '../../../components';
import { Color, Style } from '../../../constants';
import { color } from '../../../constants/color.css';
import TicketBadge from '../../client-tickets/components/TicketBadge';
import { type Idea } from '../board';

type ContentProps = {
  idea: Idea;
  canManage: boolean;
  grip: React.ReactNode;
  href: string;
  overlay?: boolean;
  onOpen: () => void;
  onMove: (() => void) | null;
  onLink: (() => void) | null;
  onCancel: (() => void) | null;
  onReopen: (() => void) | null;
};

/**
 * The idea card's presentation, shared by the sortable card, the cancelled
 * cards (which cannot be dragged) and the drag overlay.
 */
export function IdeaCardContent({
  idea,
  canManage,
  grip,
  href,
  overlay,
  onOpen,
  onMove,
  onLink,
  onCancel,
  onReopen,
}: ContentProps) {
  const cancelled = idea.state === 'cancelled';

  return (
    <html.div
      style={[
        styles.card,
        cancelled && styles.cardCancelled,
        overlay && styles.cardOverlay,
      ]}
      onClick={onOpen}
    >
      <html.div style={[Style.row, styles.topRow]}>
        <html.span style={styles.titleWrap}>
          <html.a
            href={href}
            style={styles.titleLink}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpen();
            }}
          >
            <Text
              typeface="body.medium"
              color={cancelled ? 'neutral60' : 'neutral100'}
            >
              {idea.title}
            </Text>
          </html.a>
        </html.span>
        {cancelled ? (
          <html.div style={styles.actions} onClick={(e) => e.stopPropagation()}>
            <TicketBadge
              label="Cancelled"
              bgColor={Color.blueGreyBg}
              textColor={Color.blueGrey}
            />
            {canManage && onReopen && (
              <DropdownMenu
                anchorSide="right"
                trigger={
                  <html.button
                    style={styles.menuButton}
                    aria-label="Idea actions"
                  >
                    <DotsThreeVerticalIcon size={18} color={Color.neutral60} />
                  </html.button>
                }
              >
                {(close: () => void) => (
                  <DropdownMenuItem
                    showRadio={false}
                    onClick={() => {
                      close();
                      onReopen();
                    }}
                  >
                    Reopen idea
                  </DropdownMenuItem>
                )}
              </DropdownMenu>
            )}
          </html.div>
        ) : (
          <html.div style={styles.actions} onClick={(e) => e.stopPropagation()}>
            {grip}
            {canManage && (
              <DropdownMenu
                anchorSide="right"
                trigger={
                  <html.button
                    style={styles.menuButton}
                    aria-label="Idea actions"
                  >
                    <DotsThreeVerticalIcon size={18} color={Color.neutral60} />
                  </html.button>
                }
              >
                {(close: () => void) => (
                  <>
                    {onMove && (
                      <DropdownMenuItem
                        showRadio={false}
                        onClick={() => {
                          close();
                          onMove();
                        }}
                      >
                        {idea.state === 'preparing'
                          ? 'Move to Ideas'
                          : 'Move to Preparing'}
                      </DropdownMenuItem>
                    )}
                    {onLink && (
                      <DropdownMenuItem
                        showRadio={false}
                        onClick={() => {
                          close();
                          onLink();
                        }}
                      >
                        Link to service order…
                      </DropdownMenuItem>
                    )}
                    {onCancel && (
                      <>
                        <html.div style={styles.menuDivider} />
                        <html.button
                          style={styles.cancelItem}
                          onClick={() => {
                            close();
                            onCancel();
                          }}
                        >
                          Cancel idea
                        </html.button>
                      </>
                    )}
                  </>
                )}
              </DropdownMenu>
            )}
          </html.div>
        )}
      </html.div>

      {idea.body && (
        <html.span style={styles.description}>
          <Text
            typeface="body.small.regular"
            color={cancelled ? 'neutral40' : 'neutral60'}
          >
            {idea.body}
          </Text>
        </html.span>
      )}

      <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
        <html.div style={[Style.row, Style.alignCenter, styles.author]}>
          <Avatar
            user={idea.author}
            size={24}
            style={cancelled ? styles.avatarDimmed : undefined}
          />
          {idea.author && (
            <Text typeface="caption.medium" color="neutral60">
              {idea.author.firstName} {idea.author.lastName}
            </Text>
          )}
        </html.div>
        {(idea.commentCount ?? 0) > 0 && (
          <html.div style={[Style.row, Style.alignCenter, styles.commentCount]}>
            <ChatCircleIcon size={14} color={color.neutral60} />
            <Text typeface="caption.medium" color="neutral60">
              {idea.commentCount}
            </Text>
          </html.div>
        )}
      </html.div>
    </html.div>
  );
}

type Props = {
  idea: Idea;
  href: string;
  canReorder: boolean;
  canManage: boolean;
  onOpen: (idea: Idea) => void;
  onMove: ((idea: Idea) => void) | null;
  onLink: ((idea: Idea) => void) | null;
  onCancel: ((idea: Idea) => void) | null;
  onReopen: ((idea: Idea) => void) | null;
};

export default function IdeaCard({
  idea,
  href,
  canReorder,
  canManage,
  onOpen,
  onMove,
  onLink,
  onCancel,
  onReopen,
}: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: idea.id, disabled: !canReorder });

  // dnd-kit's role/tabIndex are re-declared below against the RSD button props.
  const { role, tabIndex, ...draggableAttributes } = attributes;

  const grip = canReorder ? (
    <html.button
      ref={setActivatorNodeRef}
      {...draggableAttributes}
      {...listeners}
      style={styles.gripButton}
      aria-label="Reorder idea"
    >
      <DotsSixVerticalIcon size={18} color={Color.neutral40} />
    </html.button>
  ) : null;

  return (
    <html.div
      ref={setNodeRef}
      style={[
        styles.sortable,
        transform ? styles.transform(transform.x, transform.y) : undefined,
        transition ? styles.transition(transition) : undefined,
      ]}
    >
      <html.div style={isDragging ? styles.hiddenContent : undefined}>
        <IdeaCardContent
          idea={idea}
          href={href}
          canManage={canManage}
          grip={grip}
          onOpen={() => onOpen(idea)}
          onMove={onMove ? () => onMove(idea) : null}
          onLink={onLink ? () => onLink(idea) : null}
          onCancel={onCancel ? () => onCancel(idea) : null}
          onReopen={onReopen ? () => onReopen(idea) : null}
        />
      </html.div>
      {isDragging && <html.div style={styles.dropPlaceholder} />}
    </html.div>
  );
}

const styles = css.create({
  sortable: {
    position: 'relative',
    touchAction: 'manipulation',
  },
  hiddenContent: {
    visibility: 'hidden',
  },
  dropPlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: color.neutral20,
    borderRadius: 12,
    backgroundColor: color.neutral10,
  },
  transform: (x: number, y: number) => ({
    transform: `translate3d(${x}px, ${y}px, 0)`,
  }),
  transition: (value: string) => ({
    transition: value,
  }),
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
    cursor: 'pointer',
  },
  cardCancelled: {
    backgroundColor: color.neutral5,
  },
  cardOverlay: {
    borderColor: color.accent,
    boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.16)',
    cursor: 'grabbing',
  },
  topRow: {
    alignItems: 'flex-start',
    gap: 8,
  },
  titleWrap: {
    flex: 1,
    minWidth: 0,
  },
  titleLink: {
    color: 'inherit',
    textDecoration: 'none',
  },
  avatarDimmed: {
    opacity: 0.4,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  gripButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    flexShrink: 0,
    backgroundColor: color.neutral5,
    borderWidth: 0,
    borderRadius: 8,
    cursor: 'grab',
    touchAction: 'none',
    ':active': {
      cursor: 'grabbing',
    },
  },
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    flexShrink: 0,
    backgroundColor: color.neutral5,
    borderWidth: 0,
    borderRadius: 8,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  cancelItem: {
    width: '100%',
    paddingInline: 16,
    paddingBlock: 12,
    borderWidth: 0,
    backgroundColor: '#fff',
    color: color.accent,
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 1.4,
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  description: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  author: {
    gap: 8,
    minWidth: 0,
  },
  commentCount: {
    gap: 4,
    flexShrink: 0,
  },
  menuDivider: {
    height: 1,
    backgroundColor: color.neutral20,
  },
});
